var _extends = Object.assign || function (target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = arguments[i];
    for (var key in source) {
      if (Object.prototype.hasOwnProperty.call(source, key)) {
        target[key] = source[key];
      }
    }
  }
  return target;
};
function _objectWithoutProperties(obj, keys) {
  var target = {};
  for (var i in obj) {
    if (keys.indexOf(i) >= 0) continue;
    if (!Object.prototype.hasOwnProperty.call(obj, i)) continue;
    target[i] = obj[i];
  }
  return target;
}
function _classCallCheck(instance, Constructor) {
  if (!(instance instanceof Constructor)) {
    throw new TypeError("Cannot call a class as a function");
  }
}
function _possibleConstructorReturn(self, call) {
  if (!self) {
    throw new ReferenceError("this hasn't been initialised - super() hasn't been called");
  }
  return call && (typeof call === "object" || typeof call === "function") ? call : self;
}
function _inherits(subClass, superClass) {
  if (typeof superClass !== "function" && superClass !== null) {
    throw new TypeError("Super expression must either be null or a function, not " + typeof superClass);
  }
  subClass.prototype = Object.create(superClass && superClass.prototype, {
    constructor: {
      value: subClass,
      enumerable: false,
      writable: true,
      configurable: true
    }
  });
  if (superClass) Object.setPrototypeOf ? Object.setPrototypeOf(subClass, superClass) : subClass.__proto__ = superClass;
}
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import addDate from '../utils/dates/addDate';
import chunk from '../utils/chunk';
import getLastDay from '../utils/dates/getLastDay';
import stripTime from '../utils/dates/stripTime';
import toDayOfWeek from '../utils/dates/toDayOfWeek';
import CalendarDate from './CalendarDate';

/**
 * This component renders a month for the calendar view of the `DatePicker`.
 */

var CalendarMonth = function (_PureComponent) {
  _inherits(CalendarMonth, _PureComponent);
  function CalendarMonth() {
    _classCallCheck(this, CalendarMonth);
    return _possibleConstructorReturn(this, _PureComponent.apply(this, arguments));
  }
  CalendarMonth.prototype.render = function render() {
    var _props = this.props,
      calendarDate = _props.calendarDate,
      calendarTempDate = _props.calendarTempDate,
      onCalendarDateClick = _props.onCalendarDateClick,
      minDate = _props.minDate,
      maxDate = _props.maxDate,
      DateTimeFormat = _props.DateTimeFormat,
      locales = _props.locales,
      className = _props.className,
      firstDayOfWeek = _props.firstDayOfWeek,
      disableWeekEnds = _props.disableWeekEnds,
      dateClassName = _props.dateClassName,
      getDateClassName = _props.getDateClassName,
      dateRenderer = _props.dateRenderer,
      showAllDays = _props.showAllDays,
      outerDateClassName = _props.outerDateClassName,
      disableOuterDates = _props.disableOuterDates,
      props = _objectWithoutProperties(_props, ['calendarDate', 'calendarTempDate', 'onCalendarDateClick', 'minDate', 'maxDate', 'DateTimeFormat', 'locales', 'className', 'firstDayOfWeek', 'disableWeekEnds', 'dateClassName', 'getDateClassName', 'dateRenderer', 'showAllDays', 'outerDateClassName', 'disableOuterDates']);
    var days = [];
    var firstDay = stripTime(calendarDate);
    firstDay.setDate(1);
    var lastDay = getLastDay(calendarDate);
    var currentDate = toDayOfWeek(firstDay, 0);

    // changing with DST and different timezones gets tricky... setting
    // to noon might fix the date comparison below since it should be
    // more than the DST time difference
    var endDate = toDayOfWeek(lastDay, 6);
    endDate.setHours(12);
    var activeDateTime = stripTime(calendarTempDate).getTime();
    var todayTime = new Date().getTime();
    if (firstDayOfWeek) {
      currentDate = addDate(currentDate, firstDayOfWeek > firstDay.getDay() ? firstDayOfWeek - 7 : firstDayOfWeek, 'D');
      endDate = addDate(endDate, firstDayOfWeek > lastDay.getDay() ? firstDayOfWeek - 7 : firstDayOfWeek, 'D');
    }
    while (currentDate <= endDate) {
      var key = currentDate.getMonth() + '-' + currentDate.getDate();
      var currentMonth = currentDate.getMonth() === calendarDate.getMonth();
      var date = void 0;
      if (currentMonth || showAllDays) {
        var _cn;
        var time = currentDate.getTime();
        var isMinDateDisabled = minDate && minDate.getTime() > time;
        var isMaxDateDisabled = maxDate && maxDate.getTime() < time;
        var isWeekendDisabled = disableWeekEnds && (currentDate.getDay() === 0 || currentDate.getDay() === 6);
        var disabled = !currentMonth && disableOuterDates || isMinDateDisabled || isMaxDateDisabled || isWeekendDisabled;
        date = React.createElement(CalendarDate, {
          key: key,
          className: cn(dateClassName, (_cn = {}, _cn[outerDateClassName] = !currentMonth && outerDateClassName, _cn)),
          today: time === todayTime,
          getDateClassName: getDateClassName,
          dateRenderer: dateRenderer,
          active: time === activeDateTime,
          disabled: disabled,
          onClick: onCalendarDateClick,
          date: currentDate,
          DateTimeFormat: DateTimeFormat,
          locales: locales
        });
      } else {
        date = React.createElement('div', {
          key: key,
          className: 'md-calendar-date',
          role: 'gridcell'
        });
      }
      days.push(date);
      currentDate = addDate(currentDate, 1, 'D');
    }
    var weeks = chunk(days, 7);
    return React.createElement('div', _extends({
      role: 'grid',
      className: cn('md-calendar-month', className)
    }, props), weeks.map(function (week, i) {
      return React.createElement('div', {
        key: i,
        role: 'row'
      }, week);
    }));
  };
  return CalendarMonth;
}(PureComponent);
CalendarMonth.propTypes = {
  /**
   * A className to apply.
   */
  className: PropTypes.string,
  /**
   * The current selected date of the calendar. This is
   * the date after hitting the Ok button or `value` || `defaultValue`.
   */
  calendarDate: PropTypes.instanceOf(Date).isRequired,
  /**
   * The current selected date of the calendar before verifying
   * the new date.
   */
  calendarTempDate: PropTypes.instanceOf(Date).isRequired,
  /**
   * An optional min date for the calendar. This will disable any
   * dates that come before this date in the month.
   */
  minDate: PropTypes.instanceOf(Date),
  /**
   * An optional max date for the calendar. This will disable any
   * dates that come after this date in the month.
   */
  maxDate: PropTypes.instanceOf(Date),
  /**
   * A function to call that will select a new date.
   */
  onCalendarDateClick: PropTypes.func.isRequired,
  /**
   * The first day of week: 0 for Sunday, 1 for Monday, 2 for Tuesday, and so on.
   */
  firstDayOfWeek: PropTypes.oneOf([0, 1, 2, 3, 4, 5, 6]),
  /**
   * True if weekends are to be greyed out.
   */
  disableWeekEnds: PropTypes.bool,
  /**
   * An optional className to apply to a date.
   */
  dateClassName: PropTypes.string,
  /**
   * An optional function to provide class for each date in calendar.
   */
  getDateClassName: PropTypes.func,
  /**
  * An optional function to render each date component.
  */
  dateRenderer: PropTypes.func,
  /**
   * True if dates from adjacent months should be shown.
   */
  showAllDays: PropTypes.bool,
  disableOuterDates: PropTypes.bool,
  /**
   * An optional className to apply to a date from an adjacent month.
   */
  outerDateClassName: PropTypes.string,
  DateTimeFormat: PropTypes.func.isRequired,
  locales: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired
};
CalendarMonth.defaultProps = {
  firstDayOfWeek: 0
};
export default CalendarMonth;